import 'react-widgets/styles.css';

import type { AutocompleteCustomHybridProps } from './types';

import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

import type { Theme } from '@mui/material/styles';
import { Tooltip, TextField, Autocomplete, Paper, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const SearchBoxWrapper = styled('div')({
  position: 'absolute',
  top: '10px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '80%',
  maxWidth: '600px',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.vars.palette.background.paper,
  borderRadius: '4px',
  boxShadow: theme.customShadows.z8,
  marginTop: '4px',
  maxHeight: '200px',
  overflow: 'auto',
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
}));

export const AutocompleteCustomHybrid = ({ 
  onPlaceSelect, 
  defaultPlace, value, onChange,
  tooltipText,
  placeholderText 
}: AutocompleteCustomHybridProps) => {
  const { t } = useTranslation();
  const map = useMap();
  const places = useMapsLibrary('places');

  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [predictionResults, setPredictionResults] = useState<Array<google.maps.places.AutocompletePrediction>>([]);
  const [inputValue, setInputValue] = useState<string>(defaultPlace || '');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!places || !map) return;

    setAutocompleteService(new google.maps.places.AutocompleteService());
    setPlacesService(new places.PlacesService(map));
    setSessionToken(new places.AutocompleteSessionToken());

    return () => {
      setAutocompleteService(null);
      setPlacesService(null);
    };
  }, [map, places]);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const fetchPredictions = useCallback(
    async (input: string) => {
      if (!autocompleteService || !input) {
        setPredictionResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await autocompleteService.getPlacePredictions({
          input,
          sessionToken,
        });
        setPredictionResults(response.predictions);
      } catch (error) {
        setPredictionResults([]);
        console.error('Error fetching predictions:', error);
      } finally {
        setLoading(false);
      }
    },
    [autocompleteService, sessionToken]
  );

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      setInputValue(newValue);
      onChange?.(newValue);
      fetchPredictions(newValue);
    },
    [fetchPredictions, onChange]
  );

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: string | google.maps.places.AutocompletePrediction | null) => {
      if (!places || !value || typeof value === 'string') {
        onPlaceSelect(null);
        return;
      }

      setLoading(true);

      const detailRequestOptions = {
        placeId: value.place_id,
        fields: ['geometry', 'name', 'formatted_address', 'address_components', 'place_id'],
        sessionToken,
      };

      if (!placesService) {
        console.error('Places service not initialized');
        setLoading(false);
        return;
      }

      placesService.getDetails(detailRequestOptions, (
        placeDetails: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && placeDetails && placeDetails.geometry?.location) {
          onPlaceSelect(placeDetails);
          setInputValue(placeDetails.formatted_address ?? '');
          onChange?.(placeDetails.formatted_address ?? '');
          setSessionToken(new places.AutocompleteSessionToken());
        } else {
          console.error('Error getting place details:', status);
          onPlaceSelect(null);
        }

        setLoading(false);
      });
    },
    [onPlaceSelect, places, placesService, sessionToken, onChange]
  );

  return (
    <SearchBoxWrapper>
      <Tooltip title={tooltipText || defaultPlace || t('form.address.search.tooltip')} arrow placement="top">
        <Autocomplete
          freeSolo
          loading={loading}
          value={inputValue}
          inputValue={inputValue}
          options={predictionResults}
          getOptionLabel={(option) => 
            typeof option === 'string' ? option : option.description
          }
          onInputChange={handleInputChange}
          onChange={handleSelect}
          isOptionEqualToValue={(option, value) =>
            typeof option === 'string' && typeof value === 'string'
              ? option === value
              : typeof option !== 'string' && typeof value !== 'string'
              ? option.place_id === value.place_id
              : false
          }
          PaperComponent={StyledPaper}
          PopperComponent={StyledPopper}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={placeholderText || t('form.address.search.placeholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: '4px',
                  boxShadow: (theme: Theme) => theme.customShadows.z8,
                  height: '40px',
                },
              }}
            />
          )}
        />
      </Tooltip>
    </SearchBoxWrapper>
  );
}; 